import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import {
  adminListAllOrders,
  adminUpdateOrderStatus,
} from "@/lib/api/admin-credits-orders.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/all-orders")({
  component: AllOrdersPage,
});

type Order = {
  kind: "credit" | "cv" | "contract" | "subscription";
  id: string;
  reference: string;
  user_id: string;
  user_email: string | null;
  user_name: string | null;
  status: string;
  amount: number | null;
  quantity: any;
  created_at: string;
};

const STATUSES = ["all", "pending", "paid", "failed", "cancelled"] as const;
const KINDS = ["all", "credit", "cv", "contract", "subscription"] as const;

function statusColor(s: string) {
  switch ((s ?? "").toLowerCase()) {
    case "paid": return "bg-emerald-100 text-emerald-800";
    case "failed": return "bg-rose-100 text-rose-800";
    case "cancelled": return "bg-zinc-200 text-zinc-700";
    default: return "bg-amber-100 text-amber-800";
  }
}

function AllOrdersPage() {
  const list = useServerFn(adminListAllOrders);
  const update = useServerFn(adminUpdateOrderStatus);
  const qc = useQueryClient();
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("all");
  const [kind, setKind] = useState<(typeof KINDS)[number]>("all");
  const [search, setSearch] = useState("");

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["admin-all-orders", status, kind, search],
    queryFn: () => list({ data: { status, kind, search } }),
  });

  const mut = useMutation({
    mutationFn: (args: { kind: Order["kind"]; id: string; status: "pending" | "paid" | "failed" | "cancelled" }) =>
      update({ data: args }),
    onSuccess: () => {
      toast.success("Order updated");
      qc.invalidateQueries({ queryKey: ["admin-all-orders"] });
    },
    onError: (e: any) => toast.error("Update failed", { description: e?.message }),
  });

  const orders: Order[] = data?.orders ?? [];

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Admin</p>
          <h1 className="font-display text-2xl text-navy">All client orders</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Unified view of credit, CV, contract and subscription orders.
            Update statuses or jump into a specific tracker.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:border-navy"
        >
          <RefreshCw className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <div className="flex flex-wrap gap-3 rounded-lg border border-border bg-card p-3">
        <select value={kind} onChange={(e) => setKind(e.target.value as any)} className="rounded-md border border-input bg-background px-2 py-1.5 text-sm">
          {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value as any)} className="rounded-md border border-input bg-background px-2 py-1.5 text-sm">
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search reference / email / name…"
          className="flex-1 min-w-[200px] rounded-md border border-input bg-background px-2 py-1.5 text-sm"
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2">Reference</th>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">Detail</th>
              <th className="px-3 py-2">Amount</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isFetching && orders.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>
            )}
            {!isFetching && orders.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">No orders.</td></tr>
            )}
            {orders.map((o) => (
              <tr key={`${o.kind}:${o.id}`} className="border-t border-border/60">
                <td className="px-3 py-2 text-xs">{new Date(o.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs uppercase">{o.kind}</td>
                <td className="px-3 py-2 font-mono text-xs">{o.reference}</td>
                <td className="px-3 py-2 text-xs">
                  <div>{o.user_name ?? "—"}</div>
                  <div className="text-muted-foreground">{o.user_email ?? "—"}</div>
                </td>
                <td className="px-3 py-2 text-xs">{String(o.quantity ?? "")}</td>
                <td className="px-3 py-2 text-xs">{o.amount != null ? `R${o.amount}` : "—"}</td>
                <td className="px-3 py-2"><span className={`rounded px-2 py-0.5 text-xs ${statusColor(o.status)}`}>{o.status}</span></td>
                <td className="px-3 py-2">
                  <select
                    defaultValue=""
                    disabled={mut.isPending}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) return;
                      mut.mutate({ kind: o.kind, id: o.id, status: v as any });
                      e.target.value = "";
                    }}
                    className="rounded border border-input bg-background px-1 py-1 text-xs"
                  >
                    <option value="">Mark as…</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
